/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { forwardRef, useCallback, useState } from 'react';

import Alert from 'bundles/authentication/shared/Alert';

type OwnProps = {
  onInvalid?: () => void;
  onSubmit?: (values: Values) => void;
  constraints?: Record<string, Validation>;
};

type Props = OwnProps & Omit<React.ComponentPropsWithRef<'form'>, 'css' | keyof OwnProps>;

type Validation = (value: string) => string | false;

type Values = Record<string, string>;

function data(form: HTMLFormElement): Record<string, string> {
  const formElements = Array.from(form.elements);
  const result: Record<string, string> = {};
  (formElements as HTMLInputElement[])
    .filter((element) => element.name && !element.disabled)
    .map((element) => [element.name, element.value])
    .forEach(([name, val]) => {
      result[name] = val;
    });

  return result;
}

function validate(values: Record<string, string>, constraints: Record<string, Validation>): string[] {
  return (
    Object.entries(constraints)
      .map(([key, cb]) => cb(values[key] || ''))
      // I don't know why typescript doesn't understand that filter (string|false)[] => string[]
      .filter(Boolean) as string[]
  );
}

const styles = {
  error: css`
    list-style: none;
    margin: 0;
    padding: 0;
  `,
};

const Form = forwardRef<HTMLFormElement, Props>(
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  ({ children, constraints, onInvalid = () => {}, onSubmit = () => {}, ...props }, ref) => {
    const [errors, setErrors] = useState<string[]>([]);

    const handleSubmit = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
      (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const values = data(event.target as HTMLFormElement);

        const validation = validate(values, constraints || {});
        setErrors(validation);

        if (validation.length === 0) {
          onSubmit(values);
        } else if (onInvalid) {
          onInvalid();
        }
      },
      [constraints, onInvalid, onSubmit, setErrors]
    );

    return (
      <form
        {...props}
        // eslint-disable-next-line no-script-url
        action="javascript:void(0)"
        noValidate
        ref={ref}
        onSubmit={handleSubmit}
      >
        <div aria-live="assertive">
          {errors.length > 0 && (
            <Alert>
              <ul css={styles.error}>
                {errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}
        </div>
        {children}
      </form>
    );
  }
);

export default Form;
